import { useCallback, useMemo } from "react";

import { DateRangePicker, type DateRangeValue } from "nazare-ui/components";

import { useWorkflowDefinitions } from "../../../workflows-api";
import { DATE_RANGE_PRESETS } from "../../../date-range-presets.ts";
import {
  WORKFLOW_HISTORY_STATUS_VALUES,
  workflowHistoryDateBounds,
  type WorkflowHistoryUrlState,
} from "../../../workflow-history-url-state";
import { WorkflowFilterDropdown, type WorkflowFilterOption } from "./runs-table";
import type { WorkflowHistoryPeriod } from "./workflow-history-chart";

const HISTORY_PERIOD_PRESETS = [
  { value: "1h", label: "Last hour" },
  ...DATE_RANGE_PRESETS,
] as const;

const HISTORY_STATUS_PROJECTIONS: Record<string, (typeof WORKFLOW_HISTORY_STATUS_VALUES)[number]> = {
  queued: "pending",
  sleeping: "running",
  timed_out: "failed",
};

const HISTORY_STATUS_LABELS: Record<(typeof WORKFLOW_HISTORY_STATUS_VALUES)[number], string> = {
  pending: "Pending",
  running: "Running",
  completed: "Completed",
  failed: "Failed",
  cancelled: "Cancelled",
};

const RAW_RUN_STATUS_FALLBACKS: WorkflowFilterOption[] = [
  { value: "sleeping", label: "Sleeping" },
  { value: "timed_out", label: "Timed out" },
  { value: "rerun", label: "Rerun" },
];

function dateFromUrl(value?: string): Date | undefined {
  return value ? new Date(`${value}T00:00:00`) : undefined;
}

function dateToUrl(value: Date | undefined): string | undefined {
  if (!value || Number.isNaN(value.getTime())) return undefined;
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function WorkflowHistoryFilterControls({
  filters,
  asOf,
  onFiltersChange,
  statusMode = "history",
  dateLabel = "Filter workflow history by date",
}: {
  filters: WorkflowHistoryUrlState;
  asOf?: string;
  onFiltersChange: (update: Partial<WorkflowHistoryUrlState>) => void;
  statusMode?: "history" | "raw";
  dateLabel?: string;
}) {
  const definitions = useWorkflowDefinitions();
  const workflowOptions = useMemo<WorkflowFilterOption[]>(
    () => (definitions.data?.definitions ?? []).map((item) => ({ value: item.name, label: item.label })),
    [definitions.data?.definitions],
  );
  const statusOptions = useMemo<WorkflowFilterOption[]>(
    () => {
      const statuses = definitions.data?.statuses ?? [];
      if (statusMode === "raw") {
        const options = statuses.map((item) => ({ value: item.value, label: item.label }));
        const values = new Set(options.map((option) => option.value));
        return [
          ...options,
          ...RAW_RUN_STATUS_FALLBACKS.filter((option) => !values.has(option.value)),
        ];
      }

      const seen = new Set<string>();
      return statuses.flatMap((item) => {
        const value = HISTORY_STATUS_PROJECTIONS[item.value] ?? item.value;
        if (
          !WORKFLOW_HISTORY_STATUS_VALUES.includes(
            value as (typeof WORKFLOW_HISTORY_STATUS_VALUES)[number],
          ) ||
          seen.has(value)
        ) {
          return [];
        }
        seen.add(value);
        const canonicalValue = value as (typeof WORKFLOW_HISTORY_STATUS_VALUES)[number];
        return [{ value: canonicalValue, label: HISTORY_STATUS_LABELS[canonicalValue] }];
      });
    },
    [definitions.data?.statuses, statusMode],
  );
  const historyDateBounds = useMemo(() => {
    const asOfDate = asOf ? new Date(asOf) : new Date();
    const bounds = workflowHistoryDateBounds(Number.isNaN(asOfDate.getTime()) ? new Date() : asOfDate);
    return {
      minDate: dateFromUrl(bounds.min),
      maxDate: dateFromUrl(bounds.max),
    };
  }, [asOf]);
  const dateRange = useMemo<DateRangeValue>(() => ({
    from: dateFromUrl(filters.from),
    to: dateFromUrl(filters.to),
  }), [filters.from, filters.to]);
  const onRangeChange = useCallback((next: DateRangeValue) => {
    const from = dateToUrl(next?.from);
    const to = dateToUrl(next?.to);
    if (!from) {
      onFiltersChange({ period: "24h", from: "", to: "" });
      return;
    }
    if (!to || from > to) {
      onFiltersChange({
        period: filters.period === "custom" ? "24h" : filters.period,
        from,
        to: "",
      });
      return;
    }
    onFiltersChange({ period: "custom", from, to });
  }, [filters.period, onFiltersChange]);
  const rangeLabel = useMemo(() => {
    const preset = HISTORY_PERIOD_PRESETS.find((option) => option.value === filters.period);
    return preset?.label ?? "Custom range";
  }, [filters.period]);

  return (
    <>
      <DateRangePicker
        value={dateRange}
        label={dateLabel}
        valueLabel={filters.period === "custom" ? undefined : rangeLabel}
        presets={HISTORY_PERIOD_PRESETS}
        selectedPreset={filters.period}
        onPresetChange={(period) => onFiltersChange({
          period: period as WorkflowHistoryPeriod,
          from: "",
          to: "",
        })}
        onChange={onRangeChange}
        minDate={historyDateBounds.minDate}
        maxDate={historyDateBounds.maxDate}
      />
      <WorkflowFilterDropdown
        options={workflowOptions}
        values={filters.workflow}
        onChange={(values) => onFiltersChange({ workflow: values })}
        label="Filter by workflow"
        emptyLabel="All workflows"
      />
      <WorkflowFilterDropdown
        options={statusOptions}
        values={filters.status}
        onChange={(values) => onFiltersChange({ status: values })}
        label="Filter by status"
        emptyLabel="All statuses"
      />
    </>
  );
}
